import {
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Spacing,
  Tag,
  Typography,
  message,
} from "@p0security/ui";
import {
  PillSelect,
  type PillSelectOption,
} from "components/common/PillSelect";
import { useIdentityProviders } from "hooks/useIdentityProviders";
import { useCallback, useMemo, useState } from "react";

import { MCP_CLIENT_PLATFORM_OPTIONS } from "./clientPlatforms";
import type {
  AgenticClientRegistration,
  AgenticRegisteredClient,
} from "./types";

// Accept SemVer core versions with optional prerelease and build metadata.
const SEMANTIC_VERSION_PATTERN =
  /^(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)(?:-[\dA-Za-z-]+(?:\.[\dA-Za-z-]+)*)?(?:\+[\dA-Za-z-]+(?:\.[\dA-Za-z-]+)*)?$/;

const CredentialValue: React.FC<{
  label: string;
  secret?: boolean;
  value: string;
}> = ({ label, secret = false, value }) => {
  const [secretVisible, setSecretVisible] = useState(false);
  const toggleSecretVisibility = useCallback(
    () => setSecretVisible((visible) => !visible),
    []
  );

  return (
    <Card size="small">
      <Flex gap={8} vertical>
        <Flex align="center" gap={8}>
          <Typography.Text strong type="secondary">
            {label}
          </Typography.Text>
          {secret && <Tag color="red">Secret</Tag>}
        </Flex>
        <Flex align="center" gap={8}>
          <Typography.Text
            className="break-all font-mono text-[13px]"
            copyable={{ text: value }}
          >
            {secret && !secretVisible ? "••••••••••••••••••••••••" : value}
          </Typography.Text>
          {secret && (
            <Button
              aria-label={
                secretVisible ? "Hide client secret" : "Show client secret"
              }
              icon={secretVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              size="small"
              type="text"
              onClick={toggleSecretVisibility}
            />
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

const CLIENT_TYPE_OPTIONS: PillSelectOption<
  AgenticClientRegistration["type"]
>[] = [
  {
    description: "An agent using a specific gateway client",
    icon: <RobotOutlined />,
    label: "Gateway client",
    value: "client_credential_post",
  },
  {
    description: "An agent federated by an identity provider",
    icon: <SafetyCertificateOutlined />,
    label: "Federated",
    value: "jwt_bearer",
  },
];

/** Guidance on which claim to enrol, keyed by the shape of a provider's issuer.
 *
 * Providers differ in which claim identifies an agent, and an identity that
 * does not match the one P0 derives from the token never authenticates. Hint at
 * the right value where the issuer lets us infer it.
 */
const identityHint = (issuer: string | undefined) => {
  const grouping =
    "If the provider issues an `external_sub` claim to group several subjects into one agent, use that value instead.";
  if (issuer?.includes("accounts.google.com")) {
    return `For a GCP service account, use its email, such as agent@my-project.iam.gserviceaccount.com. ${grouping}`;
  }
  if (issuer?.includes("token.actions.githubusercontent.com")) {
    return `For GitHub Actions, use the token subject, such as repo:my-org/my-repo:ref:refs/heads/main. ${grouping}`;
  }
  return `Use the identity as the provider presents it: a verified email if its tokens carry one, otherwise the token subject (\`sub\`). ${grouping}`;
};

export type ClientRegistrationModalProps = {
  clearError: () => void;
  error?: string;
  loading: boolean;
  onClose: () => void;
  onRegister: (
    registration: AgenticClientRegistration
  ) => Promise<AgenticRegisteredClient | undefined>;
  open: boolean;
};

/** Registers an agentic client, presenting whatever the new client needs:
 * one-time credentials for a confidential client, or the issued subject for a
 * federated agent identity. */
export const ClientRegistrationModal: React.FC<
  ClientRegistrationModalProps
> = ({ clearError, error, loading, onClose, onRegister, open }) => {
  // Owned here rather than passed in: the provider list is only ever read by
  // this form, and the enclosing views have no use for it.
  const {
    error: identityProvidersError,
    loading: identityProvidersLoading,
    providers: identityProviders,
  } = useIdentityProviders();
  const [form] = Form.useForm<AgenticClientRegistration>();
  const [registered, setRegistered] = useState<AgenticRegisteredClient>();
  const clientType = Form.useWatch("type", form) ?? "client_credential_post";
  const providerId = Form.useWatch("providerId", form);

  const providerOptions = useMemo(
    () =>
      identityProviders.map(({ id }) => ({
        label: id,
        value: id,
      })),
    [identityProviders]
  );

  const selectedProvider = useMemo(
    () => identityProviders.find(({ id }) => id === providerId),
    [identityProviders, providerId]
  );

  // The `name` binding on the Form.Item already reads and writes `type`.
  // PillSelect requires `value` and `onChange`, so give it the value the form
  // holds and a handler that makes the same write.
  const selectClientType = useCallback(
    (type: AgenticClientRegistration["type"]) =>
      form.setFieldValue("type", type),
    [form]
  );

  const close = useCallback(() => {
    if (loading) return;
    setRegistered(undefined);
    clearError();
    form.resetFields();
    onClose();
  }, [clearError, form, loading, onClose]);

  const submit = useCallback(
    async (values: AgenticClientRegistration) => {
      const next = await onRegister(
        values.type === "jwt_bearer"
          ? { ...values, identity: values.identity.trim() }
          : {
              ...values,
              hostname: values.hostname.trim(),
              redirectUri: values.redirectUri.trim(),
              version: values.version.trim(),
            }
      );
      if (next) setRegistered(next);
    },
    [onRegister]
  );

  // An agent identity needs an installed provider. Without one the form can
  // never pass validation, so do not offer the action.
  const noProviderAvailable =
    clientType === "jwt_bearer" &&
    !identityProvidersLoading &&
    providerOptions.length === 0;

  const credentials =
    registered && registered.type !== "jwt_bearer" ? registered : undefined;
  const federated =
    registered && registered.type === "jwt_bearer" ? registered : undefined;

  const copyCredentials = useCallback(async () => {
    if (!credentials) return;
    try {
      if (!navigator.clipboard) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(
        `Client ID: ${credentials.id}\nClient secret: ${credentials.secret}`
      );
      message.success("Credentials copied");
    } catch {
      message.error("Could not copy credentials. Copy each value manually.");
    }
  }, [credentials]);

  // Once a client is registered there is nothing left to submit, so the default
  // Cancel/OK footer gives way to whatever ends the flow: confirming the secret
  // was saved, or simply dismissing.
  const footer = credentials
    ? [
        <Button icon={<CopyOutlined />} key="copy" onClick={copyCredentials}>
          Copy both
        </Button>,
        <Button key="done" type="primary" onClick={close}>
          I saved these credentials
        </Button>,
      ]
    : federated
      ? [
          <Button key="done" type="primary" onClick={close}>
            Done
          </Button>,
        ]
      : undefined;

  return (
    <Modal
      cancelButtonProps={{ disabled: loading }}
      cancelText="Cancel"
      centered
      closable={!credentials}
      confirmLoading={loading}
      destroyOnHidden
      footer={footer}
      keyboard={!credentials}
      maskClosable={!credentials && !loading}
      okButtonProps={{ disabled: noProviderAvailable }}
      okText={
        clientType === "jwt_bearer"
          ? "Register agent identity"
          : "Register client"
      }
      onCancel={close}
      onOk={form.submit}
      open={open}
      title={
        registered
          ? federated
            ? "Agent identity registered"
            : "Client registered"
          : "Register client"
      }
      width={620}
    >
      {federated ? (
        <Flex gap={16} vertical>
          <span className="sr-only" role="status">
            Agent identity registered.
          </span>
          <Alert
            description="The agent can now connect through the gateway using a token from this provider. No secret is issued for a federated identity."
            message="Identity enrolled"
            showIcon
            type="success"
          />
          <CredentialValue label="Issued subject" value={federated.id} />
          <CredentialValue
            label="Identity provider"
            value={federated.providerId}
          />
          <Alert
            icon={<LockOutlined />}
            message="If the agent cannot connect, compare this subject with the caller shown on the Event Log tab: the identity must match the claim its provider actually issues."
            showIcon
            type="info"
          />
        </Flex>
      ) : credentials ? (
        <Flex gap={16} vertical>
          <span className="sr-only" role="status">
            Client registered. Save the credentials before closing this window.
          </span>
          <Alert
            description="The client secret cannot be retrieved again. Store both values in your approved secret manager before closing this window."
            message="Save these credentials now"
            showIcon
            type="warning"
          />
          <CredentialValue label="Client ID" value={credentials.id} />
          <CredentialValue
            label="Client secret"
            secret
            value={credentials.secret}
          />
          <Alert
            icon={<LockOutlined />}
            message="These credentials identify the registered client. The human acting user still signs in separately through OAuth."
            showIcon
            type="info"
          />
        </Flex>
      ) : (
        <>
          <Typography.Paragraph type="secondary">
            Create an identity permitted to connect through the gateway. A
            registration identifies the client or agent, not the OAuth user
            acting during a session.
          </Typography.Paragraph>
          {error && (
            <Spacing asChild mb={16}>
              <Alert message={error} showIcon type="error" />
            </Spacing>
          )}
          <Form<AgenticClientRegistration>
            className="[&_.ant-form-item]:mb-4"
            form={form}
            initialValues={{
              platform: "claude-code",
              type: "client_credential_post",
            }}
            layout="vertical"
            onFinish={submit}
            requiredMark="optional"
          >
            <Form.Item
              extra={
                CLIENT_TYPE_OPTIONS.find(({ value }) => value === clientType)
                  ?.description
              }
              label="Client type"
              name="type"
              rules={[{ message: "Select a client type.", required: true }]}
            >
              <PillSelect<AgenticClientRegistration["type"]>
                options={CLIENT_TYPE_OPTIONS}
                placeholder="Select a client type"
                value={clientType}
                onChange={selectClientType}
              />
            </Form.Item>
            {clientType === "jwt_bearer" ? (
              <>
                <Form.Item
                  extra={
                    identityProvidersError ||
                    identityProvidersLoading ||
                    providerOptions.length > 0
                      ? "The provider whose tokens this agent will present."
                      : "No identity providers are installed. Install one on the identity-provider integration first."
                  }
                  label="Identity provider"
                  name="providerId"
                  rules={[
                    {
                      message: "Select an identity provider.",
                      required: true,
                    },
                  ]}
                >
                  <Select
                    loading={identityProvidersLoading}
                    notFoundContent={
                      identityProvidersLoading
                        ? "Loading identity providers…"
                        : (identityProvidersError ??
                          "No installed identity providers")
                    }
                    options={providerOptions}
                    placeholder="Select an identity provider"
                    status={identityProvidersError ? "error" : undefined}
                    virtual={false}
                  />
                </Form.Item>
                <Form.Item
                  extra={identityHint(selectedProvider?.issuer)}
                  label="Agent identity"
                  name="identity"
                  rules={[
                    {
                      message: "Enter the agent identity.",
                      required: true,
                      whitespace: true,
                    },
                  ]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="agent@my-project.iam.gserviceaccount.com"
                  />
                </Form.Item>
              </>
            ) : (
              <ConfidentialClientFields />
            )}
          </Form>
        </>
      )}
    </Modal>
  );
};

/** The fields that describe a specific installation of an agentic tool. */
const ConfidentialClientFields: React.FC = () => (
  <>
    <Form.Item
      label="Platform"
      name="platform"
      rules={[{ message: "Select a client platform.", required: true }]}
    >
      <Select options={MCP_CLIENT_PLATFORM_OPTIONS} virtual={false} />
    </Form.Item>
    <Form.Item
      extra="The machine or runtime where this client will run."
      label="Hostname"
      name="hostname"
      rules={[
        {
          message: "Enter the client hostname.",
          required: true,
          whitespace: true,
        },
      ]}
    >
      <Input autoComplete="off" placeholder="agent-runner-01" />
    </Form.Item>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
      <Form.Item
        label="Client version"
        name="version"
        rules={[
          {
            message: "Enter the client version.",
            required: true,
            whitespace: true,
          },
          {
            message: "Enter a semantic version, such as 1.0.0.",
            pattern: SEMANTIC_VERSION_PATTERN,
          },
        ]}
      >
        <Input autoComplete="off" placeholder="1.0.0" />
      </Form.Item>
      <Form.Item
        extra="OAuth callback for this client."
        label="Redirect URI"
        name="redirectUri"
        rules={[
          {
            message: "Enter an OAuth redirect URI.",
            required: true,
            whitespace: true,
          },
          {
            message: "Enter a valid URI, including its scheme.",
            pattern: /^[a-z][a-z\d+.-]*:\S+$/i,
          },
        ]}
      >
        <Input
          autoComplete="off"
          placeholder="http://localhost:8765/callback"
        />
      </Form.Item>
    </div>
  </>
);
